import type {
  AssetDefinition,
  GameModuleValidationError,
  GameModuleValidationResult,
  GameModuleValidationWarning,
  ThriveGameModule,
} from '../sdk';
import { AUDIO_CATEGORIES } from '../sdk/theme';
import { INPUT_CAPABILITIES } from '../shared/types/capabilities';
import type { AssetId } from '../shared/types/ids';
import type { PlayerMode, PlayerRuntimeState } from '../shared/types/players';
import type { GameplayTunables } from '../shared/types/tunables';
import { DEFAULT_GAMEPLAY_TUNABLES } from '../platform/config/tunables';
import { assetIdVersion, isValidAssetId } from '../shared/validation/asset-id';
import { isValidSkillId } from '../shared/validation/skill-id';

/**
 * Generic contract validation, run before any module is launched (module spec §20,
 * architecture §8).
 *
 * It never throws — not for a malformed module, and not for a hostile one. A module is
 * arbitrary imported code, so every property read is treated as capable of throwing: a
 * `get metadata() { throw }` accessor produces a structured failure exactly like a missing
 * field would. "This game is broken" must always be able to render as a friendly screen.
 *
 * Game-specific rules stay in the module's own `validate()`, whose result is merged in here.
 */

const GAME_ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const VERSION_PATTERN = /^\d+\.\d+\.\d+(?:-[0-9a-z.-]+)?$/i;

const PRESENTATION_MODES = new Set(['AR_CAMERA', 'RENDERED_SCENE', 'HYBRID']);
const PLAYER_MODES = new Set(['SINGLE', 'COOP_SIMULTANEOUS_SIMPLE']);
const ACTIVITY_LEVELS = new Set(['LOW', 'MEDIUM', 'HIGH']);
const DISPLAY_TARGETS = new Set(['LAPTOP', 'TV']);
const METADATA_STATUSES = new Set(['development', 'playtest', 'released']);
const SUBJECT_DOMAINS = new Set([
  'math',
  'language',
  'logic',
  'memory',
  'movement',
  'music',
  'science',
]);
const ASSET_TYPES = new Set(['image', 'svg', 'sprite', 'rive', 'audio']);
const ASSET_FORMATS = new Set(['png', 'svg', 'webp', 'json', 'mp3', 'ogg', 'wav']);
/**
 * `wav` joins the playable formats for the Phase 5 Slice 0″ placeholders (D-094(3)).
 *
 * They are synthesised locally by `tools/audio/`, which is what keeps third-party bytes and their
 * licences out of the repo before the sourcing slice; producing `ogg`/`mp3` instead would have
 * meant adding an encoder to the toolchain to make art nobody has approved. Real audio arrives
 * compressed, and both compressed formats were already here.
 */
const AUDIO_FORMATS = new Set(['mp3', 'ogg', 'wav']);
/**
 * What a manifest may say a sound is for (PS5-2; `narration` added in Slice A″).
 *
 * Read from the SDK rather than written again here. A hand-kept copy is what made Spider Math
 * unlaunchable the first time a third category existed: the type allowed it, this list did not, and
 * nothing in the unit or contract suites compared them.
 */
const AUDIO_CATEGORY_VALUES = new Set<string>(AUDIO_CATEGORIES);
const ASSET_CATEGORIES = new Set([
  'hero',
  'guide',
  'boss',
  'creature',
  'ar',
  'portal',
  'target',
  'collectible',
  'environment',
  'ui',
  'effect',
  'audio',
]);
const ASSET_APPROVAL_STATUSES = new Set([
  'draft',
  'reviewing',
  'approved',
  'needs-revision',
  'deprecated',
]);
const CAPABILITIES = new Set<string>(INPUT_CAPABILITIES);
const MISSION_STAGE_TYPES = new Set([
  'INTRO',
  'TUTORIAL',
  'CHALLENGE',
  'STORY',
  'COOPERATIVE',
  'BOSS',
  'REWARD',
  'SUMMARY',
]);
const COMPLETION_RULE_TYPES = new Set([
  'ALL_STAGES_COMPLETE',
  'ALL_CONTENT_RESOLVED',
  'ROUND_COUNT',
  'CUSTOM',
]);
const REWARD_KINDS = new Set(['BADGE', 'STAMP', 'COSMETIC']);
const EXPECTED_RESPONSE_MODES = new Set([
  'SEMANTIC_ACTION',
  'ACTION_COUNT',
  'BODY_ZONE',
  'TARGET_WORD',
  'RHYTHM_PATTERN',
  'COMPOSITE',
]);
const CONTENT_INPUT_METHODS = new Set(['body-zone', 'count-action', 'hands-or-crouch']);
const CONTENT_ITEM_KINDS = new Set(['ACADEMIC', 'SHARED_MOVEMENT']);
const SKILL_BANDS = new Set(['explorer', 'hero', 'master']);

/** Every provider method the platform will actually call, by provider field. */
const REQUIRED_PROVIDER_METHODS: Readonly<Record<string, readonly string[]>> = {
  missionProvider: ['listMissions', 'getMission'],
  contentProvider: ['getContent', 'validateContent'],
  scoringStrategy: ['initialState', 'applyEvent', 'getVisibleScore', 'getCompletionReward'],
  adaptationPolicy: ['getStartingState', 'selectDifficulty', 'updateState', 'getBounds'],
  teachingProvider: ['getTeachingMoment'],
  parentSummaryAdapter: ['buildSummary'],
  progressionProvider: [
    'createInitialProgress',
    'applySession',
    'getAvailableMissions',
    'getGameRewards',
  ],
};

/** Optional hooks: absent is fine, present-but-not-callable is not. */
const OPTIONAL_HOOKS = ['preload', 'dispose'] as const;

const OPTIONAL_PROVIDER_METHODS: Readonly<Record<string, readonly string[]>> = {
  contentPackProvider: ['listInstalledPacks', 'validatePack', 'registerPack'],
};

class Collector {
  readonly errors: GameModuleValidationError[] = [];
  readonly warnings: GameModuleValidationWarning[] = [];

  error(code: string, message: string, path?: string): void {
    this.errors.push({ code, message, ...(path === undefined ? {} : { path }) });
  }

  warn(code: string, message: string, path?: string): void {
    this.warnings.push({ code, message, ...(path === undefined ? {} : { path }) });
  }

  result(): GameModuleValidationResult {
    return { valid: this.errors.length === 0, errors: this.errors, warnings: this.warnings };
  }
}

function describe(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

/**
 * Runs one validation section in isolation. A property accessor that throws fails only its own
 * section, so a hostile module cannot hide the rest of its problems behind a single trap.
 */
function guarded<T>(collect: Collector, section: string, run: () => T, fallback: T): T {
  try {
    return run();
  } catch (cause) {
    collect.error(
      'MODULE_THREW_DURING_VALIDATION',
      `Reading "${section}" threw: ${describe(cause)}`,
      section,
    );
    return fallback;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isPositiveInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

function nonEmptyArrayOf(value: unknown, allowed: ReadonlySet<string>): boolean {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((entry) => typeof entry === 'string' && allowed.has(entry))
  );
}

function validateMetadata(
  candidate: Record<string, unknown>,
  declaredAssets: ReadonlySet<AssetId>,
  collect: Collector,
): void {
  const metadata = candidate['metadata'];
  if (!isRecord(metadata)) {
    collect.error('METADATA_MISSING', 'Module metadata is missing or not an object.', 'metadata');
    return;
  }

  for (const field of ['title', 'shortTitle', 'description', 'thumbnailAssetId']) {
    if (!nonEmptyString(metadata[field])) {
      collect.error(
        'METADATA_FIELD_EMPTY',
        `metadata.${field} must be a non-empty string.`,
        `metadata.${field}`,
      );
    }
  }

  const ageBands = metadata['ageBands'];
  if (!Array.isArray(ageBands) || ageBands.length === 0) {
    collect.error(
      'METADATA_AGE_BANDS',
      'metadata.ageBands must list at least one age band.',
      'metadata.ageBands',
    );
  }

  if (!nonEmptyArrayOf(metadata['subjectDomains'], SUBJECT_DOMAINS)) {
    collect.error(
      'METADATA_SUBJECT_DOMAINS',
      'metadata.subjectDomains must list at least one known subject domain.',
      'metadata.subjectDomains',
    );
  }

  if (typeof metadata['status'] !== 'string' || !METADATA_STATUSES.has(metadata['status'])) {
    collect.error(
      'METADATA_STATUS',
      'metadata.status must be development, playtest or released.',
      'metadata.status',
    );
  }

  if (!Array.isArray(metadata['tags'])) {
    collect.error('METADATA_TAGS', 'metadata.tags must be an array.', 'metadata.tags');
  }

  // The catalogue shows these before the theme is loaded, so a module pointing its own
  // metadata at art it never declared is a manifest bug, not a runtime fallback case.
  for (const field of ['thumbnailAssetId', 'heroAssetId']) {
    const assetId = metadata[field];
    if (assetId === undefined) continue;
    if (typeof assetId !== 'string' || !declaredAssets.has(assetId)) {
      collect.error(
        'METADATA_ASSET_UNDECLARED',
        `metadata.${field} points at "${String(assetId)}", which the theme manifest does not declare.`,
        `metadata.${field}`,
      );
    }
  }
}

function validateLaunchRequirements(candidate: Record<string, unknown>, collect: Collector): void {
  const launch = candidate['launchRequirements'];
  if (!isRecord(launch)) {
    collect.error(
      'LAUNCH_REQUIREMENTS_MISSING',
      'Module launchRequirements is missing or not an object.',
      'launchRequirements',
    );
    return;
  }

  if (
    typeof launch['presentationMode'] !== 'string' ||
    !PRESENTATION_MODES.has(launch['presentationMode'])
  ) {
    collect.error(
      'LAUNCH_PRESENTATION_MODE',
      'launchRequirements.presentationMode must be AR_CAMERA, RENDERED_SCENE or HYBRID.',
      'launchRequirements.presentationMode',
    );
  }

  if (!nonEmptyArrayOf(launch['supportedPlayerModes'], PLAYER_MODES)) {
    collect.error(
      'LAUNCH_PLAYER_MODES',
      'launchRequirements.supportedPlayerModes must list known player modes.',
      'launchRequirements.supportedPlayerModes',
    );
  }

  if (launch['minimumPlayers'] !== 1) {
    collect.error(
      'LAUNCH_MINIMUM_PLAYERS',
      'launchRequirements.minimumPlayers must be 1.',
      'launchRequirements.minimumPlayers',
    );
  }

  const maximumPlayers = launch['maximumPlayers'];
  if (maximumPlayers !== 1 && maximumPlayers !== 2) {
    // Two active local players is a hard platform ceiling (architecture §25.8).
    collect.error(
      'LAUNCH_MAXIMUM_PLAYERS',
      `launchRequirements.maximumPlayers must be 1 or 2, received ${String(maximumPlayers)}.`,
      'launchRequirements.maximumPlayers',
    );
  }

  const required = launch['requiredCapabilities'];
  if (
    !Array.isArray(required) ||
    !required.every((entry) => typeof entry === 'string' && CAPABILITIES.has(entry))
  ) {
    collect.error(
      'LAUNCH_REQUIRED_CAPABILITIES',
      'launchRequirements.requiredCapabilities must only contain known input capabilities.',
      'launchRequirements.requiredCapabilities',
    );
  }

  const optional = launch['optionalCapabilities'];
  if (
    optional !== undefined &&
    (!Array.isArray(optional) ||
      !optional.every((entry) => typeof entry === 'string' && CAPABILITIES.has(entry)))
  ) {
    collect.error(
      'LAUNCH_OPTIONAL_CAPABILITIES',
      'launchRequirements.optionalCapabilities must only contain known input capabilities.',
      'launchRequirements.optionalCapabilities',
    );
  }

  if (!nonEmptyArrayOf(launch['supportedActivityLevels'], ACTIVITY_LEVELS)) {
    collect.error(
      'LAUNCH_ACTIVITY_LEVELS',
      'launchRequirements.supportedActivityLevels must list LOW, MEDIUM or HIGH.',
      'launchRequirements.supportedActivityLevels',
    );
  }

  if (!nonEmptyArrayOf(launch['supportedDisplayTargets'], DISPLAY_TARGETS)) {
    collect.error(
      'LAUNCH_DISPLAY_TARGETS',
      'launchRequirements.supportedDisplayTargets must list LAPTOP or TV.',
      'launchRequirements.supportedDisplayTargets',
    );
  }

  if (typeof launch['supportsCalibrationReuse'] !== 'boolean') {
    collect.error(
      'LAUNCH_CALIBRATION_REUSE',
      'launchRequirements.supportsCalibrationReuse must be a boolean.',
      'launchRequirements.supportsCalibrationReuse',
    );
  }
}

function validateProviders(candidate: Record<string, unknown>, collect: Collector): void {
  for (const [field, methods] of Object.entries(REQUIRED_PROVIDER_METHODS)) {
    const provider = candidate[field];
    if (!isRecord(provider)) {
      collect.error('PROVIDER_MISSING', `Module is missing the mandatory ${field}.`, field);
      continue;
    }
    for (const method of methods) {
      if (typeof provider[method] !== 'function') {
        collect.error(
          'PROVIDER_METHOD_MISSING',
          `${field}.${method}() is missing or not callable.`,
          `${field}.${method}`,
        );
      }
    }
  }

  // An optional hook that exists but is not callable is worse than one that is absent: the
  // launch lifecycle would try to invoke it.
  for (const hook of OPTIONAL_HOOKS) {
    const value = candidate[hook];
    if (value !== undefined && typeof value !== 'function') {
      collect.error(
        'OPTIONAL_HOOK_NOT_CALLABLE',
        `${hook} is declared but is not a function.`,
        hook,
      );
    }
  }

  for (const [field, methods] of Object.entries(OPTIONAL_PROVIDER_METHODS)) {
    const provider = candidate[field];
    if (provider === undefined) continue;
    if (!isRecord(provider)) {
      collect.error(
        'OPTIONAL_PROVIDER_INVALID',
        `${field} is declared but is not an object.`,
        field,
      );
      continue;
    }
    for (const method of methods) {
      if (typeof provider[method] !== 'function') {
        collect.error(
          'OPTIONAL_PROVIDER_METHOD_MISSING',
          `${field}.${method}() is missing or not callable.`,
          `${field}.${method}`,
        );
      }
    }
  }

  const narration = candidate['narrationProfile'];
  if (!isRecord(narration)) {
    collect.error(
      'NARRATION_PROFILE_MISSING',
      'Module is missing the mandatory narrationProfile.',
      'narrationProfile',
    );
    return;
  }

  if (!nonEmptyString(narration['id'])) {
    collect.error(
      'NARRATION_PROFILE_ID',
      'narrationProfile.id must be a non-empty string.',
      'narrationProfile.id',
    );
  }
  if (!nonEmptyString(narration['defaultVoiceProfileId'])) {
    collect.error(
      'NARRATION_PROFILE_VOICE',
      'narrationProfile.defaultVoiceProfileId must be a non-empty string.',
      'narrationProfile.defaultVoiceProfileId',
    );
  }
  const mappings = narration['characterMappings'];
  if (mappings !== undefined && !isRecord(mappings)) {
    collect.error(
      'NARRATION_PROFILE_MAPPINGS',
      'narrationProfile.characterMappings must be an object when present.',
      'narrationProfile.characterMappings',
    );
  }
}

function validateAssetDefinition(
  gameId: string,
  assetId: AssetId,
  definition: unknown,
  path: string,
  collect: Collector,
): void {
  if (!isRecord(definition)) {
    collect.error('ASSET_DEFINITION_INVALID', `Asset "${assetId}" is not an object.`, path);
    return;
  }

  if (!isValidAssetId(assetId)) {
    collect.error(
      'ASSET_ID_FORMAT',
      `Asset id "${assetId}" must be dot-separated lowercase segments ending in .v[n] (D-014).`,
      path,
    );
  }

  if (assetId.startsWith(`${gameId}.`)) {
    collect.error(
      'ASSET_ID_GAME_PREFIX',
      `Asset id "${assetId}" must not repeat the game prefix inside its own theme package (D-014).`,
      path,
    );
  }

  if (definition['id'] !== assetId) {
    collect.error(
      'ASSET_ID_MISMATCH',
      `Asset "${assetId}" declares id "${String(definition['id'])}".`,
      path,
    );
  }

  if (!nonEmptyString(definition['source'])) {
    collect.error('ASSET_SOURCE_MISSING', `Asset "${assetId}" has no source.`, path);
  }

  if (typeof definition['type'] !== 'string' || !ASSET_TYPES.has(definition['type'])) {
    collect.error('ASSET_TYPE_INVALID', `Asset "${assetId}" has an unknown type.`, path);
  }

  if (typeof definition['category'] !== 'string' || !ASSET_CATEGORIES.has(definition['category'])) {
    collect.error('ASSET_CATEGORY_INVALID', `Asset "${assetId}" has an unknown category.`, path);
  }

  if (typeof definition['format'] !== 'string' || !ASSET_FORMATS.has(definition['format'])) {
    collect.error('ASSET_FORMAT_INVALID', `Asset "${assetId}" has an unknown format.`, path);
  }

  if (
    typeof definition['approvalStatus'] !== 'string' ||
    !ASSET_APPROVAL_STATUSES.has(definition['approvalStatus'])
  ) {
    collect.error(
      'ASSET_APPROVAL_INVALID',
      `Asset "${assetId}" has an unknown approvalStatus.`,
      path,
    );
  }

  const declaredVersion = definition['version'];
  const tokenVersion = assetIdVersion(assetId);
  if (typeof declaredVersion !== 'number' || !Number.isInteger(declaredVersion)) {
    collect.error(
      'ASSET_VERSION_INVALID',
      `Asset "${assetId}" needs an integer version field.`,
      path,
    );
  } else if (tokenVersion !== null && tokenVersion !== declaredVersion) {
    // The ID token and the version field are separate fields that must agree (D-014).
    collect.error(
      'ASSET_VERSION_MISMATCH',
      `Asset "${assetId}" declares version ${declaredVersion} but its id token says v${tokenVersion}.`,
      path,
    );
  }

  if (typeof definition['transparent'] !== 'boolean') {
    collect.error(
      'ASSET_TRANSPARENT_INVALID',
      `Asset "${assetId}" needs a boolean transparent flag.`,
      path,
    );
  }
  if (typeof definition['preload'] !== 'boolean') {
    collect.error(
      'ASSET_PRELOAD_INVALID',
      `Asset "${assetId}" needs a boolean preload flag.`,
      path,
    );
  }
}

function validateCharacters(
  manifest: Record<string, unknown>,
  declared: ReadonlySet<AssetId>,
  collect: Collector,
): void {
  const characters = manifest['characters'];
  if (!isRecord(characters)) {
    collect.error(
      'THEME_CHARACTERS_MISSING',
      'themeManifest.characters must be an object (it may be empty).',
      'themeManifest.characters',
    );
    return;
  }

  for (const [characterId, character] of Object.entries(characters)) {
    const path = `themeManifest.characters.${characterId}`;
    if (!isRecord(character)) {
      collect.error(
        'THEME_CHARACTER_INVALID',
        `Character "${characterId}" is not an object.`,
        path,
      );
      continue;
    }
    if (character['id'] !== characterId) {
      collect.error(
        'THEME_CHARACTER_ID_MISMATCH',
        `Character "${characterId}" declares id "${String(character['id'])}".`,
        path,
      );
    }
    if (!nonEmptyString(character['displayName'])) {
      collect.error(
        'THEME_CHARACTER_NAME_MISSING',
        `Character "${characterId}" needs a displayName.`,
        path,
      );
    }

    const states = character['states'];
    if (!isRecord(states) || Object.keys(states).length === 0) {
      collect.error(
        'THEME_CHARACTER_STATES_EMPTY',
        `Character "${characterId}" must declare at least one state.`,
        `${path}.states`,
      );
      continue;
    }

    for (const [state, assetId] of Object.entries(states)) {
      if (typeof assetId !== 'string' || !declared.has(assetId)) {
        collect.error(
          'THEME_CHARACTER_STATE_UNRESOLVED',
          `Character "${characterId}" state "${state}" points at an undeclared asset.`,
          `${path}.states.${state}`,
        );
      }
    }

    const defaultState = character['defaultState'];
    if (typeof defaultState !== 'string' || !(defaultState in states)) {
      collect.error(
        'THEME_CHARACTER_DEFAULT_STATE',
        `Character "${characterId}" defaultState "${String(defaultState)}" is not one of its states.`,
        `${path}.defaultState`,
      );
    }
  }
}

function validateEffects(
  manifest: Record<string, unknown>,
  declared: ReadonlySet<AssetId>,
  collect: Collector,
): void {
  const effects = manifest['effects'];
  if (!isRecord(effects)) {
    collect.error(
      'THEME_EFFECTS_MISSING',
      'themeManifest.effects must be an object (it may be empty).',
      'themeManifest.effects',
    );
    return;
  }

  for (const [effectId, effect] of Object.entries(effects)) {
    const path = `themeManifest.effects.${effectId}`;
    if (!isRecord(effect)) {
      collect.error('THEME_EFFECT_INVALID', `Effect "${effectId}" is not an object.`, path);
      continue;
    }
    if (effect['id'] !== effectId) {
      collect.error(
        'THEME_EFFECT_ID_MISMATCH',
        `Effect "${effectId}" declares id "${String(effect['id'])}".`,
        path,
      );
    }
    if (typeof effect['assetId'] !== 'string' || !declared.has(effect['assetId'])) {
      collect.error(
        'THEME_EFFECT_ASSET_UNRESOLVED',
        `Effect "${effectId}" points at an undeclared asset.`,
        `${path}.assetId`,
      );
    }
    const duration = effect['durationMs'];
    if (typeof duration !== 'number' || !Number.isFinite(duration) || duration < 0) {
      collect.error(
        'THEME_EFFECT_DURATION_INVALID',
        `Effect "${effectId}" needs a non-negative durationMs.`,
        `${path}.durationMs`,
      );
    }
    // Non-negotiable child-safety declaration: no flashing or strobing, ever
    // (architecture §23). The type says `false`; this is the runtime half of that promise.
    if (effect['flashing'] !== false) {
      collect.error(
        'THEME_EFFECT_FLASHING',
        `Effect "${effectId}" must declare flashing: false. Flashing and strobing effects are never permitted.`,
        `${path}.flashing`,
      );
    }
  }
}

function validateAudio(manifest: Record<string, unknown>, collect: Collector): void {
  const audio = manifest['audio'];
  if (!isRecord(audio)) {
    collect.error(
      'THEME_AUDIO_MISSING',
      'themeManifest.audio must be an object (it may be empty).',
      'themeManifest.audio',
    );
    return;
  }

  for (const [audioId, clip] of Object.entries(audio)) {
    const path = `themeManifest.audio.${audioId}`;
    if (!isRecord(clip)) {
      collect.error('THEME_AUDIO_INVALID', `Audio "${audioId}" is not an object.`, path);
      continue;
    }
    if (clip['id'] !== audioId) {
      collect.error(
        'THEME_AUDIO_ID_MISMATCH',
        `Audio "${audioId}" declares id "${String(clip['id'])}".`,
        path,
      );
    }
    if (!nonEmptyString(clip['source'])) {
      collect.error('THEME_AUDIO_SOURCE_MISSING', `Audio "${audioId}" has no source.`, path);
    }
    if (typeof clip['format'] !== 'string' || !AUDIO_FORMATS.has(clip['format'])) {
      collect.error(
        'THEME_AUDIO_FORMAT_INVALID',
        `Audio "${audioId}" must use an audio format (${[...AUDIO_FORMATS].join(', ')}).`,
        `${path}.format`,
      );
    }
    if (typeof clip['loop'] !== 'boolean') {
      collect.error(
        'THEME_AUDIO_LOOP_INVALID',
        `Audio "${audioId}" needs a boolean loop flag.`,
        `${path}.loop`,
      );
    }
    const volume = clip['volume'];
    if (typeof volume !== 'number' || !Number.isFinite(volume) || volume < 0 || volume > 1) {
      collect.error(
        'THEME_AUDIO_VOLUME_INVALID',
        `Audio "${audioId}" needs a volume between 0 and 1.`,
        `${path}.volume`,
      );
    }
    /**
     * The three fields `AudioDefinition` grew before its first entry (PS5-2, D-094(3)).
     *
     * Checked here for the same reason `flashing: false` is: the type states them, and a module is
     * arbitrary imported code that can state anything. A clip with no category has no level and no
     * place in the ducking order; a clip with no priority would have to be guessed at by whatever
     * plays it; and a clip with no approval state would be claiming, by omission, that somebody
     * had listened to it.
     */
    if (typeof clip['category'] !== 'string' || !AUDIO_CATEGORY_VALUES.has(clip['category'])) {
      collect.error(
        'THEME_AUDIO_CATEGORY_INVALID',
        `Audio "${audioId}" needs a category (${[...AUDIO_CATEGORY_VALUES].join(', ')}).`,
        `${path}.category`,
      );
    }
    const duckPriority = clip['duckPriority'];
    if (typeof duckPriority !== 'number' || !Number.isFinite(duckPriority)) {
      collect.error(
        'THEME_AUDIO_DUCK_PRIORITY_INVALID',
        `Audio "${audioId}" needs a finite duckPriority.`,
        `${path}.duckPriority`,
      );
    }
    if (
      typeof clip['approvalStatus'] !== 'string' ||
      !ASSET_APPROVAL_STATUSES.has(clip['approvalStatus'])
    ) {
      collect.error(
        'THEME_AUDIO_APPROVAL_INVALID',
        `Audio "${audioId}" needs an approval status (${[...ASSET_APPROVAL_STATUSES].join(', ')}).`,
        `${path}.approvalStatus`,
      );
    }
  }
}

/** Returns the set of asset IDs the manifest actually declares, for cross-checks. */
function validateThemeManifest(
  gameId: string,
  candidate: Record<string, unknown>,
  collect: Collector,
): Set<AssetId> {
  const declaredIds = new Set<AssetId>();
  const manifest = candidate['themeManifest'];
  if (!isRecord(manifest)) {
    collect.error(
      'THEME_MANIFEST_MISSING',
      'Module is missing its themeManifest.',
      'themeManifest',
    );
    return declaredIds;
  }

  if (!nonEmptyString(manifest['id'])) {
    collect.error(
      'THEME_ID_MISSING',
      'themeManifest.id must be a non-empty string.',
      'themeManifest.id',
    );
  }
  if (typeof manifest['version'] !== 'string' || !VERSION_PATTERN.test(manifest['version'])) {
    collect.error(
      'THEME_VERSION_INVALID',
      'themeManifest.version must look like 1.2.3.',
      'themeManifest.version',
    );
  }

  const assets = manifest['assets'];
  const ui = manifest['ui'];
  if (!isRecord(assets) || Object.keys(assets).length === 0) {
    collect.error(
      'THEME_ASSETS_EMPTY',
      'themeManifest.assets must declare at least one asset.',
      'themeManifest.assets',
    );
  }
  if (!isRecord(ui)) {
    collect.error('THEME_UI_MISSING', 'themeManifest.ui must be an object.', 'themeManifest.ui');
  }

  const declared = new Map<AssetId, AssetDefinition>();
  for (const [group, record] of [
    ['assets', assets],
    ['ui', ui],
  ] as const) {
    if (!isRecord(record)) continue;
    for (const [assetId, definition] of Object.entries(record)) {
      validateAssetDefinition(
        gameId,
        assetId,
        definition,
        `themeManifest.${group}.${assetId}`,
        collect,
      );
      if (isRecord(definition)) {
        declared.set(assetId, definition as unknown as AssetDefinition);
        declaredIds.add(assetId);
      }
    }
  }

  for (const [assetId, definition] of declared) {
    const fallback = definition.fallbackAssetId;
    if (fallback !== undefined && !declared.has(fallback)) {
      collect.error(
        'THEME_FALLBACK_UNRESOLVED',
        `Asset "${assetId}" falls back to "${fallback}", which the manifest does not declare.`,
        `themeManifest.assets.${assetId}.fallbackAssetId`,
      );
    }
    if (fallback === assetId) {
      collect.error(
        'THEME_FALLBACK_SELF',
        `Asset "${assetId}" falls back to itself, which is not a fallback.`,
        `themeManifest.assets.${assetId}.fallbackAssetId`,
      );
    }
  }

  const placeholders = manifest['categoryPlaceholders'];
  if (!isRecord(placeholders) || Object.keys(placeholders).length === 0) {
    // Without these, an unresolved ID has nowhere to land (D-014).
    collect.error(
      'THEME_PLACEHOLDERS_MISSING',
      'themeManifest.categoryPlaceholders must declare at least one generic placeholder.',
      'themeManifest.categoryPlaceholders',
    );
  } else {
    for (const [category, assetId] of Object.entries(placeholders)) {
      if (!ASSET_CATEGORIES.has(category)) {
        collect.error(
          'THEME_PLACEHOLDER_CATEGORY',
          `categoryPlaceholders has an unknown category "${category}".`,
          `themeManifest.categoryPlaceholders.${category}`,
        );
        continue;
      }
      if (typeof assetId !== 'string' || !declared.has(assetId)) {
        collect.error(
          'THEME_PLACEHOLDER_UNRESOLVED',
          `Placeholder for "${category}" points at "${String(assetId)}", which the manifest does not declare.`,
          `themeManifest.categoryPlaceholders.${category}`,
        );
      }
    }
  }

  validateCharacters(manifest, declaredIds, collect);
  validateEffects(manifest, declaredIds, collect);
  validateAudio(manifest, collect);

  return declaredIds;
}

function mergeModuleSelfValidation(candidate: Record<string, unknown>, collect: Collector): void {
  const validate = candidate['validate'];
  if (typeof validate !== 'function') {
    collect.error('VALIDATE_MISSING', 'Module must expose validate().', 'validate');
    return;
  }

  let result: unknown;
  try {
    result = (validate as () => unknown).call(candidate);
  } catch (cause) {
    // A throwing validator is itself a validation failure, never an app crash.
    collect.error('VALIDATE_THREW', `Module validate() threw: ${describe(cause)}`, 'validate');
    return;
  }

  if (!isRecord(result) || typeof result['valid'] !== 'boolean') {
    collect.error(
      'VALIDATE_RESULT_INVALID',
      'Module validate() must return a GameModuleValidationResult.',
      'validate',
    );
    return;
  }

  const errors = Array.isArray(result['errors']) ? result['errors'] : [];
  const warnings = Array.isArray(result['warnings']) ? result['warnings'] : [];

  for (const entry of errors) {
    if (!isRecord(entry)) continue;
    collect.error(
      String(entry['code'] ?? 'MODULE_SELF_VALIDATION'),
      String(entry['message'] ?? 'Module reported a validation error.'),
      typeof entry['path'] === 'string' ? entry['path'] : undefined,
    );
  }
  for (const entry of warnings) {
    if (!isRecord(entry)) continue;
    collect.warn(
      String(entry['code'] ?? 'MODULE_SELF_VALIDATION'),
      String(entry['message'] ?? 'Module reported a validation warning.'),
      typeof entry['path'] === 'string' ? entry['path'] : undefined,
    );
  }

  if (result['valid'] === false && errors.length === 0) {
    collect.error(
      'VALIDATE_INVALID_WITHOUT_REASON',
      'Module validate() reported invalid but listed no errors.',
      'validate',
    );
  }
}

/**
 * Static contract validation.
 *
 * Accepts `unknown` on purpose: a lazily imported module is untyped at runtime, and the whole
 * point of this function is to decide whether it is a `ThriveGameModule` at all.
 */
export function validateGameModule(candidate: unknown): GameModuleValidationResult {
  const collect = new Collector();

  try {
    if (!isRecord(candidate)) {
      collect.error('MODULE_NOT_OBJECT', 'A game module must be an object.');
      return collect.result();
    }

    const gameId = guarded(
      collect,
      'id',
      () => (typeof candidate['id'] === 'string' ? candidate['id'] : ''),
      '',
    );
    if (!GAME_ID_PATTERN.test(gameId)) {
      collect.error(
        'MODULE_ID_INVALID',
        `Module id "${gameId}" must be lowercase kebab-case.`,
        'id',
      );
    }

    guarded(
      collect,
      'version',
      () => {
        const version = candidate['version'];
        if (typeof version !== 'string' || !VERSION_PATTERN.test(version)) {
          collect.error(
            'MODULE_VERSION_INVALID',
            'Module version must look like 1.2.3.',
            'version',
          );
        }
      },
      undefined,
    );

    const declaredAssets = guarded(
      collect,
      'themeManifest',
      () => validateThemeManifest(gameId, candidate, collect),
      new Set<AssetId>(),
    );
    guarded(
      collect,
      'metadata',
      () => validateMetadata(candidate, declaredAssets, collect),
      undefined,
    );
    guarded(
      collect,
      'launchRequirements',
      () => validateLaunchRequirements(candidate, collect),
      undefined,
    );
    guarded(collect, 'providers', () => validateProviders(candidate, collect), undefined);
    guarded(collect, 'validate', () => mergeModuleSelfValidation(candidate, collect), undefined);
  } catch (cause) {
    // Belt and braces: no path out of this function throws, whatever the module does.
    collect.error(
      'MODULE_THREW_DURING_VALIDATION',
      `Validating the module threw: ${describe(cause)}`,
    );
  }

  return collect.result();
}

function validateMissionDefinition(
  mission: unknown,
  requestedId: string,
  declaredAssets: ReadonlySet<AssetId>,
  collect: Collector,
): void {
  const path = `missionProvider.getMission(${requestedId})`;
  if (!isRecord(mission)) {
    collect.error('MISSION_NOT_OBJECT', `${path} did not return a mission definition.`, path);
    return;
  }

  if (mission['id'] !== requestedId) {
    collect.error(
      'MISSION_ID_MISMATCH',
      `${path} returned a mission declaring id "${String(mission['id'])}".`,
      path,
    );
  }
  if (!nonEmptyString(mission['title'])) {
    collect.error('MISSION_TITLE_EMPTY', `${path} returned a mission with no title.`, path);
  }

  const stages = mission['stages'];
  if (!Array.isArray(stages) || stages.length === 0) {
    collect.error('MISSION_STAGES_EMPTY', `${path} returned a mission with no stages.`, path);
    return;
  }

  const stageIds = new Set<string>();
  for (const stage of stages) {
    if (!isRecord(stage)) {
      collect.error(
        'MISSION_STAGE_INVALID',
        `${path} contains a stage that is not an object.`,
        path,
      );
      continue;
    }

    const stageId = stage['id'];
    const stagePath = `${path}.stages.${String(stageId)}`;
    if (!nonEmptyString(stageId)) {
      collect.error(
        'MISSION_STAGE_ID_EMPTY',
        'Every mission stage needs a non-empty id.',
        stagePath,
      );
    } else if (stageIds.has(stageId)) {
      collect.error('MISSION_STAGE_ID_DUPLICATE', `Duplicate stage id "${stageId}".`, stagePath);
    } else {
      stageIds.add(stageId);
    }

    if (typeof stage['type'] !== 'string' || !MISSION_STAGE_TYPES.has(stage['type'])) {
      collect.error(
        'MISSION_STAGE_TYPE_INVALID',
        `Stage "${String(stageId)}" has an unknown type "${String(stage['type'])}".`,
        stagePath,
      );
    }

    const sceneFactory = stage['sceneFactory'];
    if (!isRecord(sceneFactory) || typeof sceneFactory['create'] !== 'function') {
      collect.error(
        'MISSION_STAGE_SCENE_FACTORY',
        `Stage "${String(stageId)}" needs a sceneFactory with a create() method.`,
        `${stagePath}.sceneFactory`,
      );
    }

    const inputs = stage['inputRequirements'];
    if (
      !Array.isArray(inputs) ||
      !inputs.every((entry) => typeof entry === 'string' && CAPABILITIES.has(entry))
    ) {
      collect.error(
        'MISSION_STAGE_INPUTS_INVALID',
        `Stage "${String(stageId)}" declares an unknown input capability.`,
        `${stagePath}.inputRequirements`,
      );
    }

    const rule = stage['completionRule'];
    if (
      !isRecord(rule) ||
      typeof rule['type'] !== 'string' ||
      !COMPLETION_RULE_TYPES.has(rule['type'])
    ) {
      collect.error(
        'MISSION_STAGE_COMPLETION_RULE',
        `Stage "${String(stageId)}" has an unknown completion rule.`,
        `${stagePath}.completionRule`,
      );
    } else if (rule['type'] === 'ROUND_COUNT' && !isPositiveInteger(rule['rounds'])) {
      collect.error(
        'MISSION_STAGE_ROUND_COUNT',
        `Stage "${String(stageId)}" declares a ROUND_COUNT rule without a positive round count.`,
        `${stagePath}.completionRule.rounds`,
      );
    }

    if (typeof stage['teachingEnabled'] !== 'boolean') {
      collect.error(
        'MISSION_STAGE_TEACHING_FLAG',
        `Stage "${String(stageId)}" needs a boolean teachingEnabled.`,
        stagePath,
      );
    }

    const policy = stage['cooperativePolicy'];
    if (policy !== undefined) {
      const policyPath = `${stagePath}.cooperativePolicy`;
      if (!isRecord(policy)) {
        collect.error('COOP_POLICY_INVALID', 'cooperativePolicy must be an object.', policyPath);
      } else {
        // The cooperative rule is locked: the first valid correct action completes the
        // challenge and the team scores exactly once (COOP_SIMULTANEOUS_SIMPLE).
        if (policy['mode'] !== 'FIRST_VALID_CORRECT') {
          collect.error(
            'COOP_POLICY_MODE',
            `cooperativePolicy.mode must be FIRST_VALID_CORRECT, received "${String(policy['mode'])}".`,
            `${policyPath}.mode`,
          );
        }
        if (policy['awardDuplicateScore'] !== false) {
          collect.error(
            'COOP_POLICY_DUPLICATE_SCORE',
            'cooperativePolicy.awardDuplicateScore must be false; a round scores once.',
            `${policyPath}.awardDuplicateScore`,
          );
        }
        const grace = policy['participationGraceMs'];
        if (typeof grace !== 'number' || !Number.isFinite(grace) || grace <= 0) {
          collect.error(
            'COOP_POLICY_GRACE',
            'cooperativePolicy.participationGraceMs must be a positive number.',
            `${policyPath}.participationGraceMs`,
          );
        }
      }
    }
  }

  const missionRule = mission['completionRule'];
  if (
    !isRecord(missionRule) ||
    typeof missionRule['type'] !== 'string' ||
    !COMPLETION_RULE_TYPES.has(missionRule['type'])
  ) {
    collect.error(
      'MISSION_COMPLETION_RULE',
      `${path} returned an unknown mission completion rule.`,
      `${path}.completionRule`,
    );
  }

  const reward = mission['rewardDefinition'];
  const rewardPath = `${path}.rewardDefinition`;
  if (!isRecord(reward)) {
    collect.error('MISSION_REWARD_MISSING', `${path} returned no rewardDefinition.`, rewardPath);
    return;
  }

  if (!nonEmptyString(reward['id'])) {
    collect.error('MISSION_REWARD_ID', 'rewardDefinition.id must be non-empty.', rewardPath);
  }
  if (typeof reward['kind'] !== 'string' || !REWARD_KINDS.has(reward['kind'])) {
    collect.error(
      'MISSION_REWARD_KIND',
      'rewardDefinition.kind must be BADGE, STAMP or COSMETIC.',
      rewardPath,
    );
  }
  if (!nonEmptyString(reward['label'])) {
    collect.error('MISSION_REWARD_LABEL', 'rewardDefinition.label must be non-empty.', rewardPath);
  }
  const rewardAsset = reward['assetId'];
  if (
    rewardAsset !== undefined &&
    (typeof rewardAsset !== 'string' || !declaredAssets.has(rewardAsset))
  ) {
    collect.error(
      'MISSION_REWARD_ASSET_UNDECLARED',
      `rewardDefinition.assetId "${String(rewardAsset)}" is not declared by the theme manifest.`,
      `${rewardPath}.assetId`,
    );
  }
}

/**
 * A hint is read mid-round, when a child is already stuck. `hints[0].id` throwing at that
 * moment is the worst possible time to discover the array held nulls, so every entry is
 * checked here rather than trusted because the array exists.
 */
function validateHints(
  hints: unknown,
  contentId: string,
  itemPath: string,
  declaredAssets: ReadonlySet<AssetId>,
  collect: Collector,
): void {
  const hintsPath = `${itemPath}.hints`;

  // Present even when empty: the teaching ladder reads this array, and `undefined.length` at
  // round 12 is not an acceptable way to find out it was missing.
  if (!Array.isArray(hints)) {
    collect.error(
      'CONTENT_ITEM_HINTS_MISSING',
      `Content "${contentId}" needs a hints array (it may be empty).`,
      hintsPath,
    );
    return;
  }

  const seen = new Set<string>();
  hints.forEach((hint, index) => {
    const hintPath = `${hintsPath}[${index}]`;

    if (!isRecord(hint)) {
      collect.error(
        'CONTENT_ITEM_HINT_INVALID',
        `Content "${contentId}" has a hint that is not an object.`,
        hintPath,
      );
      return;
    }

    const hintId = hint['id'];
    if (!nonEmptyString(hintId)) {
      collect.error(
        'CONTENT_ITEM_HINT_ID_EMPTY',
        `Content "${contentId}" has a hint with no id.`,
        `${hintPath}.id`,
      );
    } else if (seen.has(hintId)) {
      collect.error(
        'CONTENT_ITEM_HINT_ID_DUPLICATE',
        `Content "${contentId}" repeats hint id "${hintId}".`,
        `${hintPath}.id`,
      );
    } else {
      seen.add(hintId);
    }

    const afterMs = hint['afterMs'];
    if (
      afterMs !== undefined &&
      (typeof afterMs !== 'number' || !Number.isFinite(afterMs) || afterMs < 0)
    ) {
      collect.error(
        'CONTENT_ITEM_HINT_TIMING_INVALID',
        `Content "${contentId}" has a hint with a non-finite or negative afterMs.`,
        `${hintPath}.afterMs`,
      );
    }

    for (const field of ['text', 'spokenText'] as const) {
      const value = hint[field];
      if (value !== undefined && !nonEmptyString(value)) {
        collect.error(
          'CONTENT_ITEM_HINT_TEXT_INVALID',
          `Content "${contentId}" has a hint whose ${field} is present but not usable text.`,
          `${hintPath}.${field}`,
        );
      }
    }

    const visual = hint['visualAssetId'];
    if (visual !== undefined && (typeof visual !== 'string' || !declaredAssets.has(visual))) {
      collect.error(
        'CONTENT_ITEM_HINT_ASSET_UNDECLARED',
        `Content "${contentId}" has a hint referencing undeclared art "${String(visual)}".`,
        `${hintPath}.visualAssetId`,
      );
    }
  });
}

function validateContentItems(
  items: unknown,
  declaredAssets: ReadonlySet<AssetId>,
  playerMode: PlayerMode,
  collect: Collector,
): void {
  const path = `contentProvider.getContent(${playerMode})`;
  if (!Array.isArray(items)) {
    collect.error('CONTENT_NOT_ARRAY', `${path} did not return an array.`, path);
    return;
  }

  const ids = new Set<string>();
  /** `${slotId} ${band}` for every slot variant seen, so one band cannot own a slot twice. */
  const slotBandPairs = new Set<string>();
  for (const item of items) {
    if (!isRecord(item)) {
      collect.error('CONTENT_ITEM_INVALID', `${path} returned a non-object item.`, path);
      continue;
    }

    const id = item['id'];
    const itemPath = `${path}.${String(id)}`;
    if (!nonEmptyString(id)) {
      collect.error('CONTENT_ITEM_ID_EMPTY', 'Every content item needs a non-empty id.', itemPath);
    } else if (ids.has(id)) {
      collect.error('CONTENT_ITEM_ID_DUPLICATE', `Duplicate content id "${id}".`, itemPath);
    } else {
      ids.add(id);
    }

    const kind = item['kind'];
    if (kind !== undefined && (typeof kind !== 'string' || !CONTENT_ITEM_KINDS.has(kind))) {
      collect.error(
        'CONTENT_ITEM_KIND_INVALID',
        `Content "${String(id)}" declares an unknown kind "${String(kind)}".`,
        `${itemPath}.kind`,
      );
    }
    const isSharedMovement = kind === 'SHARED_MOVEMENT';

    const skillIds = item['skillIds'];
    if (
      !Array.isArray(skillIds) ||
      skillIds.length === 0 ||
      !skillIds.every((skillId) => typeof skillId === 'string' && isValidSkillId(skillId))
    ) {
      collect.error(
        'CONTENT_ITEM_SKILLS_INVALID',
        `Content "${String(id)}" must reference at least one valid hierarchical skill id.`,
        `${itemPath}.skillIds`,
      );
    }

    // The band decides which child sees this item at all; without it, adaptation has nothing
    // to select on.
    const band = item['difficultyBand'];
    if (typeof band !== 'string' || !SKILL_BANDS.has(band)) {
      collect.error(
        'CONTENT_ITEM_BAND_INVALID',
        `Content "${String(id)}" needs a difficultyBand of explorer, hero or master.`,
        `${itemPath}.difficultyBand`,
      );
    }

    // Curated slots (D-034, PS-1). A slot is a position in the mission whose band variants the
    // runtime swaps between, so an unusable or ambiguous slot binding is not a cosmetic defect:
    // it is the difference between the post-teaching simplified step finding an easier question
    // and finding nothing, mid-round, in front of a child who has just got one wrong.
    const slotId = item['slotId'];
    if (slotId !== undefined) {
      if (!nonEmptyString(slotId)) {
        collect.error(
          'CONTENT_ITEM_SLOT_ID_EMPTY',
          `Content "${String(id)}" declares a slotId that is not a non-empty string.`,
          `${itemPath}.slotId`,
        );
      } else if (typeof band === 'string' && SKILL_BANDS.has(band)) {
        const pair = `${slotId} ${band}`;
        if (slotBandPairs.has(pair)) {
          collect.error(
            'CONTENT_ITEM_SLOT_BAND_DUPLICATE',
            `Slot "${slotId}" has more than one ${band} variant; a slot holds at most one item per band.`,
            `${itemPath}.slotId`,
          );
        } else {
          slotBandPairs.add(pair);
        }
      }
    }

    const difficulty = item['difficulty'];
    if (difficulty !== undefined && !isPositiveInteger(difficulty)) {
      collect.error(
        'CONTENT_ITEM_DIFFICULTY_INVALID',
        `Content "${String(id)}" declares a non-positive difficulty step.`,
        `${itemPath}.difficulty`,
      );
    }

    const prompt = item['prompt'];
    if (!isRecord(prompt)) {
      collect.error(
        'CONTENT_ITEM_PROMPT_EMPTY',
        `Content "${String(id)}" needs a prompt.`,
        `${itemPath}.prompt`,
      );
    } else {
      if (!nonEmptyString(prompt['text'])) {
        collect.error(
          'CONTENT_ITEM_PROMPT_EMPTY',
          `Content "${String(id)}" needs prompt text.`,
          `${itemPath}.prompt.text`,
        );
      }

      // Five- to seven-year-olds are not reliably reading the screen: an item with no spoken
      // instruction is unplayable for the audience, not merely incomplete.
      if (!nonEmptyString(prompt['spokenInstruction'])) {
        collect.error(
          'CONTENT_ITEM_SPOKEN_MISSING',
          `Content "${String(id)}" needs a spokenInstruction; the child may not be able to read the prompt.`,
          `${itemPath}.prompt.spokenInstruction`,
        );
      }

      if (
        typeof prompt['inputMethod'] !== 'string' ||
        !CONTENT_INPUT_METHODS.has(prompt['inputMethod'])
      ) {
        collect.error(
          'CONTENT_ITEM_INPUT_METHOD_INVALID',
          `Content "${String(id)}" declares an unknown inputMethod "${String(prompt['inputMethod'])}".`,
          `${itemPath}.prompt.inputMethod`,
        );
      }

      const visual = prompt['visualAssetId'];
      if (visual !== undefined && (typeof visual !== 'string' || !declaredAssets.has(visual))) {
        collect.error(
          'CONTENT_ITEM_ASSET_UNDECLARED',
          `Content "${String(id)}" references undeclared art "${String(visual)}".`,
          `${itemPath}.prompt.visualAssetId`,
        );
      }
    }

    validateHints(item['hints'], String(id), itemPath, declaredAssets, collect);

    const expected = item['expectedResponse'];
    const expectedPath = `${itemPath}.expectedResponse`;
    if (!isRecord(expected)) {
      collect.error(
        'CONTENT_ITEM_EXPECTED_MISSING',
        `Content "${String(id)}" needs an expectedResponse.`,
        expectedPath,
      );
      continue;
    }
    if (typeof expected['mode'] !== 'string' || !EXPECTED_RESPONSE_MODES.has(expected['mode'])) {
      collect.error(
        'CONTENT_ITEM_EXPECTED_MODE',
        `Content "${String(id)}" has an unknown expectedResponse.mode.`,
        `${expectedPath}.mode`,
      );
    }
    if (expected['value'] === undefined || expected['value'] === null) {
      collect.error(
        'CONTENT_ITEM_EXPECTED_VALUE',
        `Content "${String(id)}" needs an expectedResponse.value to match against.`,
        `${expectedPath}.value`,
      );
    }
    if (expected['bothPlayersRequired'] === true) {
      if (!isSharedMovement) {
        // Permitted for a shared movement — sealing a portal, a victory pose — and never for a
        // question with a right answer, which the first valid correct response completes (§11).
        collect.error(
          'CONTENT_ITEM_BOTH_REQUIRED',
          `Content "${String(id)}" requires both players, which is only allowed on a SHARED_MOVEMENT item, never an academic answer.`,
          `${expectedPath}.bothPlayersRequired`,
        );
      }

      if (playerMode === 'SINGLE') {
        // Even a legitimate shared movement is unfinishable with one child in the room. A solo
        // player who cannot complete a round is stuck, and being stuck is indistinguishable
        // from failing to a five-year-old.
        collect.error(
          'CONTENT_ITEM_BOTH_REQUIRED_SINGLE',
          `Content "${String(id)}" requires both players but was offered for SINGLE play, where the second player does not exist.`,
          `${expectedPath}.bothPlayersRequired`,
        );
      }
    }
  }
}

/** A minimally valid roster for the mode being validated. */
function validationPlayers(mode: PlayerMode): PlayerRuntimeState[] {
  const hero1: PlayerRuntimeState = {
    playerId: 'validation-hero-1',
    lane: 'left',
    band: 'explorer',
    isGuest: true,
    present: true,
  };

  if (mode === 'SINGLE') return [hero1];

  return [
    hero1,
    {
      playerId: 'validation-hero-2',
      lane: 'right',
      band: 'explorer',
      isGuest: true,
      present: true,
    },
  ];
}

export interface DeepValidationOptions {
  tunables?: GameplayTunables;
}

/**
 * Static validation plus the cheap parts of the module's *behaviour*: it lists its missions,
 * builds each one, and asks for content, then checks the results.
 *
 * A module whose shape is perfect but whose `getMission` returns a mission with no stages is
 * still a module that cannot launch, and discovering that at launch time is exactly what the
 * registry exists to prevent. Nothing here plays a round or scores anything.
 *
 * Like the static pass, it never throws.
 */
export async function validateGameModuleDeep(
  candidate: unknown,
  options: DeepValidationOptions = {},
): Promise<GameModuleValidationResult> {
  const staticResult = validateGameModule(candidate);
  // Calling providers on a module that already failed its shape check would only pile noise
  // on top of the real problem.
  if (!staticResult.valid || !isRecord(candidate)) return staticResult;

  const collect = new Collector();
  collect.errors.push(...staticResult.errors);
  collect.warnings.push(...staticResult.warnings);

  const tunables = options.tunables ?? DEFAULT_GAMEPLAY_TUNABLES;
  const gameModule = candidate as unknown as ThriveGameModule;

  let declaredAssets: Set<AssetId>;
  let declaredModes: PlayerMode[];
  try {
    declaredAssets = new Set<AssetId>([
      ...Object.keys(gameModule.themeManifest.assets),
      ...Object.keys(gameModule.themeManifest.ui),
    ]);
    declaredModes = [...gameModule.launchRequirements.supportedPlayerModes];
  } catch (cause) {
    collect.error(
      'MODULE_THREW_DURING_VALIDATION',
      `Reading the module's declarations threw: ${describe(cause)}`,
      'launchRequirements',
    );
    return collect.result();
  }

  for (const playerMode of declaredModes) {
    // Providers are asked under the modes the module says it supports, with a real player
    // roster for each. Fabricating a SINGLE context for a cooperative-only game and then
    // failing it for returning no missions is the validator being wrong, not the module.
    const players = validationPlayers(playerMode);
    const listContext = {
      players,
      playerMode,
      availableCapabilities: [...INPUT_CAPABILITIES],
    };

    try {
      const summaries = await gameModule.missionProvider.listMissions(listContext);

      if (!Array.isArray(summaries) || summaries.length === 0) {
        collect.error(
          'MISSION_LIST_EMPTY',
          `missionProvider.listMissions() returned no missions for ${playerMode}, which the module declares as supported.`,
          `missionProvider.listMissions(${playerMode})`,
        );
        continue;
      }

      const seenIds = new Set<string>();
      for (const summary of summaries) {
        if (!isRecord(summary) || !nonEmptyString(summary['id'])) {
          collect.error(
            'MISSION_SUMMARY_INVALID',
            'Every mission summary needs a non-empty id.',
            `missionProvider.listMissions(${playerMode})`,
          );
          continue;
        }
        if (seenIds.has(summary['id'])) {
          collect.error(
            'MISSION_SUMMARY_ID_DUPLICATE',
            `Duplicate mission id "${summary['id']}".`,
            `missionProvider.listMissions(${playerMode})`,
          );
        }
        seenIds.add(summary['id']);
        if (!nonEmptyString(summary['title'])) {
          collect.error(
            'MISSION_SUMMARY_TITLE_EMPTY',
            `Mission "${summary['id']}" has no title.`,
            `missionProvider.listMissions(${playerMode})`,
          );
        }
      }

      for (const missionId of seenIds) {
        const mission = await gameModule.missionProvider.getMission(missionId, {
          ...listContext,
          sessionId: 'validation',
          useCuratedSequence: true,
          tunables,
        });
        validateMissionDefinition(mission, missionId, declaredAssets, collect);
      }
    } catch (cause) {
      collect.error(
        'MISSION_PROVIDER_THREW',
        `missionProvider threw during validation for ${playerMode}: ${describe(cause)}`,
        'missionProvider',
      );
    }

    try {
      const items = await gameModule.contentProvider.getContent(
        {},
        {
          players,
          playerMode,
          teamBand: 'master',
          useCuratedSequence: true,
          alreadyUsedContentIds: [],
        },
      );

      validateContentItems(items, declaredAssets, playerMode, collect);

      if (Array.isArray(items)) {
        const ownResult = gameModule.contentProvider.validateContent(items);
        if (!isRecord(ownResult) || typeof ownResult['valid'] !== 'boolean') {
          collect.error(
            'CONTENT_VALIDATION_RESULT_INVALID',
            'contentProvider.validateContent() must return a ContentValidationResult.',
            'contentProvider.validateContent',
          );
        } else if (ownResult['valid'] === false) {
          const issues = Array.isArray(ownResult['errors']) ? ownResult['errors'] : [];
          for (const issue of issues) {
            if (!isRecord(issue)) continue;
            collect.error(
              String(issue['code'] ?? 'CONTENT_SELF_VALIDATION'),
              String(issue['message'] ?? 'The module rejected its own content.'),
              `contentProvider.${String(issue['contentId'] ?? 'unknown')}`,
            );
          }
          if (issues.length === 0) {
            collect.error(
              'CONTENT_SELF_VALIDATION',
              'contentProvider.validateContent() rejected the module’s own content without saying why.',
              'contentProvider.validateContent',
            );
          }
        }
      }
    } catch (cause) {
      collect.error(
        'CONTENT_PROVIDER_THREW',
        `contentProvider threw during validation for ${playerMode}: ${describe(cause)}`,
        'contentProvider',
      );
    }
  }

  return collect.result();
}

/** Narrow a validated candidate for callers that need the typed module. */
export function isThriveGameModule(candidate: unknown): candidate is ThriveGameModule {
  return validateGameModule(candidate).valid;
}
